import { CHANNEL_ID, StringTMap } from '@@types/common';
import Notice from '@@types/Notice';

import { mainDetailUiState } from '~/stores/DetailUiState';
import { isEmpty } from 'lodash';
import { action, computed, observable } from 'mobx';
import { findIndex, mergeRight, move, prepend, propEq, propOr } from 'ramda';

import { uiState } from './UiState';

export interface ChannelNoticeInfo {
  posted: Notice | null;
  notices: Notice[];
}

class ChannelNoticesStore {
  @observable
  map: StringTMap<ChannelNoticeInfo> = {};

  @computed
  get currentPostedNotice(): Notice | null {
    return propOr({}, 'posted', this.getByChannelId(mainDetailUiState.contentsChannelId));
  }

  @computed
  get currentChannelNotices(): Notice[] | null {
    return this.getNoticesByChannelId(mainDetailUiState.contentsChannelId);
  }

  getByChannelId = (channelId) => {
    return propOr({}, channelId)(this.map);
  };

  getNoticesByChannelId = (channelId): Notice[] => {
    return propOr([], 'notices')(this.getByChannelId(channelId));
  };

  @action
  removePostedNotice = (channelId) => {
    if (this.map[channelId]) {
      delete this.map[channelId].posted;
    }
  };

  @action
  moveNotice = ({ channelId, from, to }) => {
    this.map[channelId].notices = move<Notice>(from, to, this.getNoticesByChannelId(channelId));
  };

  @action
  prependNotice = ({ channelId, notice }) => {
    if (isEmpty(this.map[channelId])) {
      this.createNewChannelInfo(channelId);
    }
    this.map[channelId].notices = prepend<Notice>(notice, this.getNoticesByChannelId(channelId));
  };

  @action
  prependOrMoveNotice = ({ channelId, notice }) => {
    const index = findIndex(propEq('noticeId', notice.noticeId), this.getNoticesByChannelId(channelId));
    if (index > -1) {
      this.moveNotice({ channelId, from: index, to: 0 });
    } else {
      this.prependNotice({ channelId, notice });
    }
  };

  @action
  setPostedNotice = ({ channelId, notice }) => {
    if (isEmpty(notice)) return;
    if (isEmpty(this.map[channelId])) {
      this.createNewChannelInfo(channelId);
    }
    this.map[channelId].posted = notice;
    this.prependOrMoveNotice({ channelId, notice });
  };

  @action
  setChannelNotices = ({ channelId, notices }) => {
    if (isEmpty(this.map[channelId])) {
      this.createNewChannelInfo(channelId);
    }
    this.map[channelId].notices = notices;
  };

  @action
  updatePostedNotice = ({ channelId, notice }) => {
    this.map[channelId].posted = mergeRight(this.map[channelId].posted, { text: notice.text });
  };

  @action
  private createNewChannelInfo = (
    channelId: CHANNEL_ID,
    noticeInfo: ChannelNoticeInfo = { posted: null, notices: [] }
  ) => {
    this.map = uiState.pickAndSetLruChannelIds(channelId, noticeInfo)(this.map);
  };
}

export default ChannelNoticesStore;

export const channelNoticesStore = new ChannelNoticesStore();
